import React from 'react';
import { ScrollText, Calendar, MapPin, ShieldCheck, Mail } from 'lucide-react';

const PROMOTER = {
  name: 'Motion Reality Sim Racing Ltd',
  address: 'Unit 9, Croft Business Park, Carrock Road, Bromborough, Wirral, CH62 3RA',
  email: 'info@motionreality.co.uk',
};

// Full T&Cs summary for both competition draws (free monthly + paid sim rig),
// covering the UK compliance essentials: closing/draw dates, age, residency,
// winner contact and verification, plus the free postal route for the paid draw.
export default function CompetitionTerms() {
  return (
    <section className="py-16 px-6 lg:px-10 border-t border-white/5">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center gap-2 mb-6">
          <ScrollText className="w-5 h-5 text-primary" />
          <p className="text-primary text-xs font-bold tracking-widest">COMPETITION TERMS &amp; CONDITIONS</p>
        </div>
        <h2 className="text-white font-black text-2xl sm:text-3xl uppercase mb-2">The Official Rules</h2>
        <p className="text-white/40 text-sm mb-8 max-w-2xl">Please read these terms before entering. By entering either draw you agree to be bound by them.</p>

        <div className="space-y-6 text-sm leading-relaxed">
          <Term icon={MapPin} title="PROMOTER">
            <p className="text-white/50">
              <span className="text-white font-bold">{PROMOTER.name}</span>, {PROMOTER.address}. Company registered in England &amp; Wales. Email:{' '}
              <a href={`mailto:${PROMOTER.email}`} className="text-primary/70 hover:text-primary underline">{PROMOTER.email}</a>.
            </p>
          </Term>

          <Term icon={Calendar} title="DRAW PERIODS & CLOSING DATES">
            <ul className="text-white/50 space-y-1.5">
              <li>• <span className="text-white">H1 draw</span> — entries accepted 1 January to 30 June. Closing date: <span className="text-white">30 June, 23:59 GMT</span>.</li>
              <li>• <span className="text-white">H2 draw</span> — entries accepted 1 July to 31 December. Closing date: <span className="text-white">31 December, 23:59 GMT</span>.</li>
              <li>• <span className="text-white">Free monthly draw</span> — entries accepted from the 1st to the last day of each calendar month, closing at 23:59 GMT on the last day.</li>
            </ul>
          </Term>

          <Term icon={Calendar} title="DRAW DATES">
            <p className="text-white/50">
              The sim rig draw takes place within <span className="text-white">7 days</span> of each closing date. The monthly free experience draw takes place on or
              shortly after the 1st of the following month. Winners are drawn at random from all eligible entries received during that period.
            </p>
          </Term>

          <Term icon={ShieldCheck} title="ELIGIBILITY — AGE & RESIDENCY">
            <ul className="text-white/50 space-y-1.5">
              <li>• Sim rig draw: open to UK residents aged <span className="text-white">18 years or over</span> only.</li>
              <li>• Free monthly experience draw: open to UK residents aged <span className="text-white">5 years or over</span>. Entrants aged 5 to 17 are eligible to enter with the <span className="text-white">express permission and consent of a parent or legal guardian</span>.</li>
              <li>• If a winner is under the age of 18, the prize must be accepted and claimed on their behalf by a parent or legal guardian, who must be present on-site throughout the entirety of the sim racing session.</li>
              <li>• Session formats, vehicle classes and rig configurations will be tailored to the driver's age and experience level (including the Young Racer 5–7 experience).</li>
              <li>• Employees of Motion Reality and their immediate families are not eligible to enter.</li>
            </ul>
          </Term>

          <Term icon={Mail} title="HOW TO ENTER">
            <ul className="text-white/50 space-y-1.5">
              <li>• <span className="text-white">Paid sim rig entry:</span> £5 per entry online at our competition page. Unlimited entries per person.</li>
              <li>• <span className="text-white">Free postal entry (sim rig draw):</span> send your details by post — no purchase necessary. One entry per stamped envelope. Postal entries are treated on exactly the same terms as paid entries and go into the same random draw. See the "Free Postal Entry" section above for what to include.</li>
              <li>• <span className="text-white">Free monthly draw:</span> one free entry per person per calendar month online.</li>
            </ul>
          </Term>

          <Term icon={Mail} title="WINNER NOTIFICATION & VERIFICATION">
            <ul className="text-white/50 space-y-1.5">
              <li>• The winner is notified by <span className="text-white">email</span> (or by post where no email was provided) within <span className="text-white">7 days</span> of the draw.</li>
              <li>• The winner must respond and claim the prize within <span className="text-white">14 days</span> of notification. If the prize is unclaimed, the promoter may conduct a re-draw.</li>
              <li>• The winner may be required to provide proof of age, identity and UK residency before the prize is released.</li>
              <li>• The winner's name (first name + initial) and region will be announced on our social channels and website.</li>
            </ul>
          </Term>

          <Term icon={ScrollText} title="THE PRIZE & GENERAL">
            <ul className="text-white/50 space-y-1.5">
              <li>• Sim rig draw prize: one complete pro-spec sim racing rig (final specification confirmed at the time of the draw). No cash alternative. Non-transferable.</li>
              <li>• Monthly draw prize: one free sim racing experience session at our Bromborough venue (subject to availability; booking required).</li>
              <li>• The promoter's decision on all matters is final. No correspondence will be entered into.</li>
              <li>• This promotion is in no way sponsored, endorsed or administered by, or associated with, Stripe, Meta, TikTok or any other payment or social platform.</li>
              <li>• Full terms are available on request by post or email to the promoter.</li>
            </ul>
          </Term>
        </div>
      </div>
    </section>
  );
}

function Term({ icon: Icon, title, children }) {
  return (
    <div className="border border-white/10 bg-[#111115] p-6">
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4 text-primary" />
        <p className="text-white/50 text-[10px] font-bold tracking-widest">{title}</p>
      </div>
      {children}
    </div>
  );
}